/**
 * Componente para mostrar el árbol de carpetas
 * @module components/panels/FolderTree
 */

import { useState } from 'react'
import { Folder as FolderType } from '@/services/folderServiceApi'
import { useFolderStore } from '@/stores'
import { 
  ChevronRight, ChevronDown, Folder, FolderOpen, 
  Plus, Edit2, Trash2, X, Check 
} from 'lucide-react'

interface FolderTreeProps {
  folders: FolderType[]
  level?: number
}

export function FolderTree({ folders, level = 0 }: FolderTreeProps) {
  return (
    <div className="space-y-1">
      {folders.map((folder) => (
        <FolderTreeItem key={folder.id} folder={folder} level={level} />
      ))}
    </div>
  )
}

function FolderTreeItem({ folder, level }: { folder: FolderType; level: number }) {
  const [isExpanded, setIsExpanded] = useState(level === 0)
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState(folder.name)
  const [isAddingChild, setIsAddingChild] = useState(false)
  const [newChildName, setNewChildName] = useState('')
  const [loading, setLoading] = useState(false)

  const activeFolderId = useFolderStore((state) => state.activeFolderId)
  const setActiveFolder = useFolderStore((state) => state.setActiveFolder)
  const getChildren = useFolderStore((state) => state.getChildren)
  const createFolder = useFolderStore((state) => state.createFolder)
  const updateFolder = useFolderStore((state) => state.updateFolder)
  const deleteFolder = useFolderStore((state) => state.deleteFolder)

  const isActive = activeFolderId === folder.id
  const children = getChildren(folder.id)
  const hasChildren = children.length > 0 || (folder._count?.children ?? 0) > 0
  const nodeCount = folder._count?.nodes ?? folder.nodes?.length ?? 0

  const handleClick = () => {
    setActiveFolder(folder.id)
    if (hasChildren) {
      setIsExpanded(!isExpanded)
    }
  }

  const handleEdit = async () => {
    if (!editName.trim() || editName === folder.name) {
      setIsEditing(false)
      setEditName(folder.name)
      return
    }

    setLoading(true)
    try {
      await updateFolder(folder.id, { name: editName.trim() })
      setIsEditing(false)
    } catch (error) {
      console.error('Error updating folder:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm(`¿Eliminar la carpeta "${folder.name}"?\n\nEsto eliminará también todas las subcarpetas.`)) {
      return
    }

    setLoading(true)
    try {
      await deleteFolder(folder.id)
    } catch (error) {
      console.error('Error deleting folder:', error)
      alert('Error al eliminar la carpeta')
    } finally {
      setLoading(false)
    }
  }

  const handleAddChild = async () => {
    if (!newChildName.trim()) return

    setLoading(true)
    try {
      await createFolder(newChildName.trim(), undefined, folder.id)
      setNewChildName('')
      setIsAddingChild(false)
      setIsExpanded(true)
    } catch (error) {
      console.error('Error creating subfolder:', error)
      alert('Error al crear subcarpeta')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      {/* Folder item */}
      <div
        className={`
          group flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer
          ${
            isActive
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
              : 'hover:bg-gray-100 dark:hover:bg-dark-700'
          }
        `}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={handleClick}
      >
        {/* Expand/Collapse icon */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              setIsExpanded(!isExpanded)
            }}
            className="p-0.5 hover:bg-gray-200 dark:hover:bg-dark-600 rounded"
          >
            {isExpanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
          </button>
        ) : (
          <div className="w-4" />
        )}

        {/* Folder icon */}
        {isExpanded ? (
          <FolderOpen className="w-4 h-4 text-yellow-600 dark:text-yellow-500" />
        ) : (
          <Folder className="w-4 h-4 text-yellow-600 dark:text-yellow-500" />
        )}

        {/* Folder name (editable) */}
        {isEditing ? (
          <div className="flex-1 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleEdit()
                if (e.key === 'Escape') {
                  setIsEditing(false)
                  setEditName(folder.name)
                }
              }}
              className="flex-1 px-1 py-0 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
              autoFocus
              disabled={loading}
            />
            <button onClick={handleEdit} disabled={loading} className="p-0.5 hover:bg-gray-200 dark:hover:bg-dark-600 rounded">
              <Check className="w-3 h-3 text-green-600" />
            </button>
            <button onClick={() => { setIsEditing(false); setEditName(folder.name) }} className="p-0.5 hover:bg-gray-200 dark:hover:bg-dark-600 rounded">
              <X className="w-3 h-3 text-red-600" />
            </button>
          </div>
        ) : (
          <div className="flex-1 flex items-center gap-2">
            <span className="text-sm font-medium truncate">{folder.name}</span>
          </div>
        )}

        {/* Node count badge */}
        {nodeCount > 0 && !isEditing && (
          <span className="text-xs bg-gray-200 dark:bg-dark-600 px-2 py-0.5 rounded-full">
            {nodeCount}
          </span>
        )}

        {/* Action buttons (visible on hover) */}
        {!isEditing && (
          <div className="hidden group-hover:flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setIsAddingChild(true)}
              className="p-0.5 hover:bg-gray-200 dark:hover:bg-dark-600 rounded"
              title="Agregar subcarpeta"
            >
              <Plus className="w-3 h-3" />
            </button>
            <button
              onClick={() => setIsEditing(true)}
              className="p-0.5 hover:bg-gray-200 dark:hover:bg-dark-600 rounded"
              title="Renombrar"
            >
              <Edit2 className="w-3 h-3" />
            </button>
            <button
              onClick={handleDelete}
              className="p-0.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-600"
              title="Eliminar"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Add child folder form */}
      {isAddingChild && (
        <div 
          className="flex items-center gap-1 py-1"
          style={{ paddingLeft: `${(level + 1) * 16 + 8}px` }}
        >
          <input
            type="text"
            value={newChildName}
            onChange={(e) => setNewChildName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddChild()
              if (e.key === 'Escape') {
                setIsAddingChild(false)
                setNewChildName('')
              }
            }}
            placeholder="Nueva subcarpeta..."
            className="flex-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
            autoFocus
            disabled={loading}
          />
          <button onClick={handleAddChild} disabled={loading || !newChildName.trim()} className="p-1 hover:bg-gray-200 dark:hover:bg-dark-600 rounded">
            <Check className="w-3 h-3 text-green-600" />
          </button>
          <button onClick={() => { setIsAddingChild(false); setNewChildName('') }} className="p-1 hover:bg-gray-200 dark:hover:bg-dark-600 rounded">
            <X className="w-3 h-3 text-red-600" />
          </button>
        </div>
      )}

      {/* Subcarpetas */}
      {hasChildren && isExpanded && children.length > 0 && (
        <div className="mt-1">
          <FolderTree folders={children} level={level + 1} />
        </div>
      )}
    </div>
  )
}
